export type ClientMainTab = "leads" | "active" | "out_of_contact";

export type PropertyViewMode = "grid" | "list";
export interface PropertyNavigationState {
  view: PropertyViewMode;
  search: string;
  neighborhoods: string[];
  management: string[];
  flags: string[];
  selectedPropertyId: string | null;
}

export interface SearchNavigationState {
  price: [number, number] | null;
  bedrooms: string[];
  minSqft: number | null;
  availableOnly: boolean | null;
  moveInDate: string | null;
  sortBy: string | null;
  neighborhoods: string[];
  amenities: string[];
  management: string[];
  maxPricePerSqft: number | null;
  noFloorPlanImage: boolean;
  selectionMode: "floorplan" | "unit" | null;
  selectedKeys: string[];
}

export const DEFAULT_PROPERTY_NAVIGATION_STATE: PropertyNavigationState = {
  view: "grid", search: "", neighborhoods: [], management: [], flags: [], selectedPropertyId: null,
};
export const DEFAULT_SEARCH_NAVIGATION_STATE: SearchNavigationState = {
  price: null, bedrooms: [], minSqft: null, availableOnly: null, moveInDate: null,
  sortBy: null, neighborhoods: [], amenities: [], management: [], maxPricePerSqft: null,
  noFloorPlanImage: false, selectionMode: null, selectedKeys: [],
};

export const resolvePropertyNavigationState = (value: PropertyNavigationState | null) =>
  value ?? { ...DEFAULT_PROPERTY_NAVIGATION_STATE };
export const resolveSearchNavigationState = (value: SearchNavigationState | null) =>
  value ?? { ...DEFAULT_SEARCH_NAVIGATION_STATE };
export const resolveSelectedPropertyForWrite = (
  deepLinkPending: boolean,
  deepLinkId: string | null,
  dialogOpen: boolean,
  selectedId: string | null
): string | null => deepLinkPending ? deepLinkId : dialogOpen ? selectedId : null;
export function filterClientsForNavigationStatus<T extends { id: string; status: string }>(
  clients: T[], status: ClientNavigationStatus | "all", invoiceClientIds: ReadonlySet<string>
): T[] {
  if (status === "all") return clients;
  if (status === "invoice_sent") return clients.filter((client) => invoiceClientIds.has(client.id));
  return clients.filter((client) => client.status === status);
}

export interface AppNavigationState {
  page: string;
  clientId: string | null;
  // Drawer tab key for a client deep link (TRAIN-3: the agent-notification
  // deep link must survive a COLD load, so the tab rides the URL, not memory).
  drawerTab: string | null;
  status: ClientNavigationStatus | null;
  clientsTab: ClientMainTab | null;
  properties: PropertyNavigationState | null;
  search: SearchNavigationState | null;
}

export type GmailCallbackOutcome =
  | "connected"
  | "error"
  | "denied"
  | "invalid_state";

export interface GmailCallbackNotice {
  message: string;
  severity: "success" | "error" | "info" | "warning";
}

const VALID_DRAWER_TABS = new Set<string>([
  "timeline", "list", "shortlist", "files", "messages",
]);

const VALID_CLIENT_TABS = new Set<ClientMainTab>([
  "leads",
  "active",
  "out_of_contact",
]);
const VALID_PAGES = new Set(["dashboard", "search", "clients", "properties", "insights", "content", "settings", "admin", "messages"]);
const MAX_URL_LENGTH = 8192;
const MAX_TEXT = 160;
const MAX_ITEMS = 40;
const SAFE_ID = /^[A-Za-z0-9_.:-]{1,160}$/;
const SAFE_CATEGORY = /^[A-Za-z][A-Za-z .&'-]{0,79}$/;
const PROPERTY_FLAGS = new Set(["pet", "specials", "commission", "favorites", "inactive", "no-image"]);
const BEDROOMS = new Set(["0", "1", "2", "3+"]);
const SEARCH_SORTS = new Set(["price", "sqft", "price_per_sqft"]);
const CLIENT_STATUSES = new Set<ClientNavigationStatus>(CLIENT_NAVIGATION_STATUS_VALUES);
const GMAIL_CALLBACK_OUTCOMES = new Set<GmailCallbackOutcome>([
  "connected",
  "error",
  "denied",
  "invalid_state",
]);
const AMENITIES = new Set(["in_unit_laundry", "walk_in_closet", "view", "kitchen_island",
  "private_outdoor", "in_unit_storage", "ceiling"]);
const cleanCanonicalDate = (value: unknown): string | null => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
    ? value
    : null;
};

const cleanText = (value: unknown, max = MAX_TEXT): string | null => {
  if (typeof value !== "string") return null;
  const cleaned = value.trim();
  return cleaned && cleaned.length <= max ? cleaned : null;
};

const cleanId = (value: unknown): string | null => {
  const cleaned = cleanText(value);
  return cleaned && SAFE_ID.test(cleaned) ? cleaned : null;
};

export const buildSelectedPropertyNavigationState = (
  value: PropertyNavigationState | null,
  propertyId: string
): PropertyNavigationState | null => {
  const selectedPropertyId = cleanId(propertyId);
  if (!selectedPropertyId) return null;
  return {
    ...resolvePropertyNavigationState(value),
    selectedPropertyId,
  };
};

const cleanList = (value: unknown, idsOnly = false): string[] => {
  if (!Array.isArray(value) || value.length > MAX_ITEMS) return [];
  return Array.from(
    new Set(
      value
        .map((item) => (idsOnly ? cleanId(item) : cleanText(item, 80)))
        .filter((item): item is string => Boolean(item))
    )
  );
};
const cleanCategoryList = (value: unknown): string[] =>
  cleanList(value).filter((item) => SAFE_CATEGORY.test(item));

const cleanNumber = (value: unknown, min = 0, max = 100000): number | null =>
  typeof value === "number" && Number.isFinite(value) && value >= min && value <= max
    ? value
    : null;

function parseJsonParam(params: URLSearchParams, name: string): unknown {
  const raw = params.get(name);
  if (!raw || raw.length > 4096) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function parseClientMainTab(value: string | null): ClientMainTab | null {
  return value && VALID_CLIENT_TABS.has(value as ClientMainTab)
    ? (value as ClientMainTab)
    : null;
}

function parseProperties(value: unknown): PropertyNavigationState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const view = v.v === "list" ? "list" : v.v === "grid" ? "grid" : null;
  if (!view) return null;
  return {
    view,
    // Free-form search can contain names/addresses; it is local-only.
    search: "",
    neighborhoods: cleanCategoryList(v.n),
    management: cleanCategoryList(v.m),
    flags: cleanList(v.f, true).filter((item) => PROPERTY_FLAGS.has(item)),
    selectedPropertyId: cleanId(v.s),
  };
}

function parseSearch(value: unknown): SearchNavigationState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const rawPrice = Array.isArray(v.p) && v.p.length === 2
    ? [cleanNumber(v.p[0]), cleanNumber(v.p[1])] as const
    : null;
  const price = rawPrice && rawPrice[0] !== null && rawPrice[1] !== null && rawPrice[0] <= rawPrice[1]
    ? [rawPrice[0], rawPrice[1]] as [number, number]
    : null;
  const mode = v.sm === "floorplan" || v.sm === "unit" ? v.sm : null;
  return {
    price,
    bedrooms: cleanList(v.b).filter((item) => BEDROOMS.has(item)),
    minSqft: cleanNumber(v.ms),
    availableOnly: typeof v.a === "boolean" ? v.a : null,
    moveInDate: cleanCanonicalDate(v.d),
    sortBy: typeof v.o === "string" && SEARCH_SORTS.has(v.o) ? v.o : null,
    neighborhoods: cleanCategoryList(v.n),
    amenities: cleanList(v.am, true).filter((item) => AMENITIES.has(item)),
    management: cleanCategoryList(v.m),
    maxPricePerSqft: cleanNumber(v.pp, 0, 1000),
    noFloorPlanImage: v.ni === true,
    selectionMode: mode,
    selectedKeys: cleanList(v.sk, true),
  };
}

export function parseAppNavigationState(search: string): AppNavigationState {
  if (search.length > MAX_URL_LENGTH) search = "";
  const params = new URLSearchParams(search);
  return {
    page: params.get("page") === "properties" && ["search", "insights"].includes(params.get("section") ?? "")
      ? params.get("section")!
      : VALID_PAGES.has(params.get("page") ?? "") ? params.get("page")! : "dashboard",
    clientId: cleanId(params.get("client")),
    drawerTab: VALID_DRAWER_TABS.has(params.get("tab") ?? "") ? params.get("tab") : null,
    status: CLIENT_STATUSES.has(params.get("status") as ClientNavigationStatus)
      ? params.get("status") as ClientNavigationStatus : null,
    clientsTab: parseClientMainTab(params.get("clientsTab")),
    properties: parseProperties(parseJsonParam(params, "properties")),
    search: parseSearch(parseJsonParam(params, "search")),
  };
}

/**
 * Compatibility preflight for backend and bookmarked Settings URLs.
 *
 * LN Pro's route owner is the `page` query parameter, not the pathname. The
 * Gmail backend deliberately redirects to `/settings?gmail=...`; normalize
 * that legacy input before React chooses its first page so the callback never
 * flashes or settles on Dashboard. Preserve every query parameter here—the
 * outcome is consumed only after Settings has mounted it.
 */
export function canonicalizeLegacyAppUrl(currentUrl: string): string {
  const url = new URL(currentUrl);
  if (url.pathname === "/settings" || url.pathname === "/settings/") {
    url.pathname = "/";
    const prior = new URLSearchParams(url.search);
    prior.delete("page");
    const canonical = new URLSearchParams({ page: "settings" });
    prior.forEach((value, key) => canonical.append(key, value));
    url.search = canonical.toString();
  }
  if (["search", "insights"].includes(url.searchParams.get("page") ?? "")) {
    url.searchParams.set("section", url.searchParams.get("page")!);
    url.searchParams.set("page", "properties");
  }
  return url.toString();
}

export function parseGmailCallbackOutcome(
  search: string
): GmailCallbackOutcome | null {
  const raw = new URLSearchParams(search).get("gmail");
  return raw && GMAIL_CALLBACK_OUTCOMES.has(raw as GmailCallbackOutcome)
    ? (raw as GmailCallbackOutcome)
    : null;
}

export function gmailCallbackNotice(
  outcome: GmailCallbackOutcome
): GmailCallbackNotice {
  switch (outcome) {
    case "connected":
      return {
        message: "Gmail connected successfully!",
        severity: "success",
      };
    case "error":
      return {
        message: "Failed to connect Gmail. Please try again.",
        severity: "error",
      };
    case "denied":
      return {
        message: "Gmail connection was cancelled.",
        severity: "info",
      };
    case "invalid_state":
      return {
        message:
          "That Gmail connection link is invalid or expired. Links are valid for 15 minutes; please start again.",
        severity: "warning",
      };
  }
}

export function gmailConnectionVerificationNotice(
  connected: boolean | null
): GmailCallbackNotice {
  if (connected) return gmailCallbackNotice("connected");
  if (connected === false) {
    return {
      message:
        "Gmail returned successfully, but no active connection was found. Please reconnect.",
      severity: "warning",
    };
  }
  return {
    message:
      "Gmail returned successfully, but LocateNow could not verify the connection. Please refresh or reconnect.",
    severity: "error",
  };
}

/** Remove only the consumed Gmail outcome and retain canonical Settings. */
export function consumeGmailCallbackOutcome(currentUrl: string): string {
  const canonical = new URL(canonicalizeLegacyAppUrl(currentUrl));
  canonical.pathname = "/";
  canonical.searchParams.set("page", "settings");
  canonical.searchParams.delete("gmail");
  return canonical.toString();
}

function compactProperties(value: PropertyNavigationState): Record<string, unknown> {
  return { v: value.view, n: value.neighborhoods.length ? value.neighborhoods : undefined,
    m: value.management.length ? value.management : undefined, f: value.flags.length ? value.flags : undefined,
    s: value.selectedPropertyId ?? undefined };
}

function compactSearch(value: SearchNavigationState): Record<string, unknown> {
  return { p: value.price ?? undefined, b: value.bedrooms.length ? value.bedrooms : undefined,
    ms: value.minSqft ?? undefined, a: value.availableOnly ?? undefined, d: value.moveInDate ?? undefined,
    o: value.sortBy ?? undefined, n: value.neighborhoods.length ? value.neighborhoods : undefined,
    am: value.amenities.length ? value.amenities : undefined, m: value.management.length ? value.management : undefined,
    pp: value.maxPricePerSqft ?? undefined, ni: value.noFloorPlanImage || undefined,
    sm: value.selectionMode ?? undefined, sk: value.selectedKeys.length ? value.selectedKeys : undefined };
}

/** Canonical URL writer used by navigation and surface state. It preserves unrelated params. */
export function writeAppNavigationState(
  currentUrl: string,
  patch: Partial<AppNavigationState>
): string {
  const url = new URL(currentUrl);
  const set = (key: string, value: string | null | undefined) =>
    value ? url.searchParams.set(key, value) : url.searchParams.delete(key);
  if ("page" in patch) {
    const page = patch.page && VALID_PAGES.has(patch.page) ? patch.page : "dashboard";
    const isProperties = ["properties", "search", "insights"].includes(page);
    set("page", isProperties ? "properties" : page);
    set("section", isProperties ? (page === "properties" ? "overview" : page) : null);
  }
  if ("clientId" in patch) set("client", cleanId(patch.clientId));
  if ("drawerTab" in patch)
    set("tab", patch.drawerTab && VALID_DRAWER_TABS.has(patch.drawerTab) ? patch.drawerTab : null);
  if ("status" in patch) set("status", patch.status && CLIENT_STATUSES.has(patch.status) ? patch.status : null);
  if ("clientsTab" in patch) set("clientsTab", patch.clientsTab ?? null);
  if ("properties" in patch) {
    const safe = patch.properties ? parseProperties(compactProperties(patch.properties)) : null;
    set("properties", safe ? JSON.stringify(compactProperties(safe)) : null);
  }
  if ("search" in patch) {
    const safe = patch.search ? parseSearch(compactSearch(patch.search)) : null;
    set("search", safe ? JSON.stringify(compactSearch(safe)) : null);
  }
  if (url.toString().length <= MAX_URL_LENGTH) return url.toString();
  // Never preserve or emit an oversized prior payload as a fallback.
  url.searchParams.delete("properties");
  url.searchParams.delete("search");
  if (url.toString().length <= MAX_URL_LENGTH) return url.toString();
  return `${url.origin}${url.pathname}`;
}

export function replaceNavigationState(patch: Partial<AppNavigationState>): void {
  if (typeof window === "undefined") return;
  const next = writeAppNavigationState(window.location.href, patch);
  if (next !== window.location.href) {
    const priorState =
      window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};
    window.history.replaceState(priorState, "", next);
  }
}

export function isAppOwnedSearchHistoryEntry(state: unknown): boolean {
  return readAppOwnedSearchHistoryBinding(state) !== undefined;
}

export function readAppOwnedSearchHistoryBinding(
  state: unknown
): { clientId: string | null } | undefined {
  if (!state || typeof state !== "object") return undefined;
  const record = state as Record<string, unknown>;
  if (record.lnproSearchEntryOwned !== true) return undefined;
  if (record.lnproSearchDraftClientId === null) return { clientId: null };
  const clientId = cleanId(record.lnproSearchDraftClientId);
  return clientId ? { clientId } : undefined;
}

/** Claim the current Search history entry after a genuine in-app edit. */
export function markSearchHistoryEntryOwned(clientId: string | null): void {
  if (typeof window === "undefined") return;
  const currentBinding = readAppOwnedSearchHistoryBinding(window.history.state);
  const safeClientId = clientId === null ? null : cleanId(clientId);
  if (clientId !== null && !safeClientId) return;
  if (currentBinding?.clientId === safeClientId) return;
  const priorState =
    window.history.state && typeof window.history.state === "object"
      ? window.history.state
      : {};
  window.history.replaceState(
    {
      ...priorState,
      lnproSearchEntryOwned: true,
      lnproSearchDraftClientId: safeClientId,
    },
    "",
    window.location.href
  );
}

/** Explicit destructive-action boundary; normal navigation keeps these fields. */
export function clearUserNavigationPersistence(href: string): string {
  const cleared = writeAppNavigationState(href, {
    clientId: null,
    drawerTab: null,
    status: null,
    clientsTab: null,
    properties: null,
    search: null,
  });
  const url = new URL(cleared);
  url.searchParams.delete("gmail");
  return url.toString();
}

// R2/N8 — BROWSER BACK MUST RESTORE THE TAB.
//
// The popstate handler read page, client, status and clientsTab from the URL
// and then hardcoded `setSelectedDrawerTab(null)`. So pressing Back onto a
// ?tab=messages entry — exactly the entry the TRAIN-3 agent-notification deep
// link creates — restored everything except the thread the agent was reading.
//
// The application lives HERE rather than inline in App.tsx on purpose: App has
// never been renderable in this test suite, so anything written inline is
// untestable by construction — which is how the hardcoded null survived. This
// function is pure over its actions object and is driven by real tests.
export interface PopStateActions {
  setPage: (page: string) => void;
  setClientId: (id: string | null) => void;
  setStatus: (status: ClientNavigationStatus | null) => void;
  setDrawerTab: (tab: string | null) => void;
  setClientsTab: (tab: ClientMainTab | null) => void;
  setPropertyOpenRequest: (request: { id: string; key: number } | null) => void;
}

export function applyPopStateNavigation(
  next: AppNavigationState,
  actions: PopStateActions,
  now: number = Date.now()
): void {
  actions.setPage(next.page);
  actions.setClientId(next.clientId);
  actions.setStatus(next.status);
  actions.setDrawerTab(next.drawerTab);
  actions.setClientsTab(next.clientsTab);
  actions.setPropertyOpenRequest(
    next.properties?.selectedPropertyId
      ? { id: next.properties.selectedPropertyId, key: now }
      : null
  );
}

/** Future map persistence contract only; no current surface claims implementation. */
export interface FutureMapNavigationState {
  view: "list" | "split" | "map";
  viewport: { latitude: number; longitude: number; zoom: number } | null;
  selectedPropertyId: string | null;
}
import { CLIENT_NAVIGATION_STATUS_VALUES, type ClientNavigationStatus } from "../components/clients/types";
export type { ClientNavigationStatus } from "../components/clients/types";
